import { motion } from "framer-motion";
import { Link, useSearchParams } from "react-router-dom";
import { CITY_CHAPTERS } from "../lib/apply-schema";
import BrandName from "../components/site/BrandName";
import InstagramProfileCard from "../components/site/InstagramProfileCard";
import {
  ArrowLeft,
  ChevronDown,
  Check,
  X,
  Users,
  Handshake,
  Sparkles,
  Coffee,
  MessageSquareText,
  Heart,
  Megaphone,
  Repeat2,
  Layers,
  Filter,
  BookOpenCheck,
  GraduationCap,
  PartyPopper,
  Moon,
  Tag,
  Info,
  Award,
  Share2,
  Store,
  Building2,
  Layers3,
  ListChecks,
  UserSearch,
  ContactRound,
  CalendarDays,
  Target,
} from "lucide-react";

/** Paragraph = string | array of (string | { b: string } | { i: string }); { b } bold, { i } italic. */
function renderRich(para, dark = false) {
  if (!Array.isArray(para)) return para;
  return para.map((seg, i) =>
    typeof seg === "string"
      ? seg
      : seg.i !== undefined
        ? <em key={i}>{seg.i}</em>
        : <strong key={i} className={`font-semibold ${dark ? "text-white" : "text-black"}`}>{seg.b}</strong>
  );
}

const STRUCTURE = [
  {
    heading: "Main Groups",
    highlight: true,
    noteHeading: "What should be avoided in the above-mentioned 1 City Members Group and 9 Professional Circle Groups?",
    note: [
      [{ b: "Promotional content intended for selling" }, " and ", { b: "social media content" }, " should be avoided in the ", { b: "Main Groups" }, ". Both can be shared in the ", { b: "Business Promotions Group" }, "."],
    ],
    items: [
      {
        icon: Megaphone,
        label: "Announcement Group",
        text: [
          [{ b: "Admin-only group for all important updates" }, ", including ", { b: "official online and offline meetups" }, " hosted by City Captains and other important community announcements. ", { b: "Please keep a check on this group" }, "."],
        ],
      },
      {
        icon: Building2,
        label: "1 City Members Group",
        groupName: (city) => `HFA | All ${city} Members`,
        text: [
          ["Introductions, ", { b: "cross-industry discussions" }, ", professional requirements, leads, referrals, work opportunities, collaborations, hiring, and relevant posts, with ", { b: "members from all Professional Circles" }, "."],
          ["Members can also share what they do, including the products or services they offer, to help others discover and connect with them."],
        ],
      },
      {
        icon: Layers,
        label: "9 Professional Circle Groups",
        text: [
          ["Focused spaces for profession-related requirements, leads, referrals, work opportunities, collaborations, and hiring with members from ", { b: "related professions and fields" }, "."],
          ["Members are encouraged to ", { b: "join only the Professional Circle groups relevant to their current work profile" }, ". For requirement of any ", { b: "cross-industry connections" }, ", please use the ", { b: "City Members group" }, "."],
        ],
        pillsHeading: "9 Professional Circle Group Names:",
        pills: [
          "Finance, Legal & Compliance",
          "Marketing & Content",
          "Tech, Software & AI",
          "Real Estate, Architecture & Interiors",
          "Education, Training & Coaching",
          "Fitness & Wellness",
          "Healthcare & Medical",
          "Events & Experiences",
          "Visual & Creative Arts",
        ],
      },
    ],
  },
  {
    heading: "Optional Groups",
    highlight: true,
    note: [
      [{ b: "Both groups" }, " are ", { b: "optional spaces" }, " that members can intentionally ", { b: "join directly" }, " from the HFA WhatsApp Community. They have ", { b: "different profile pictures" }, " from the ", { b: "Main Groups" }, " for easy identification."],
      ["The ", { b: "guidelines" }, " for the ", { b: "Main Groups (Do\u2019s & Don\u2019ts)" }, " are ", { b: "not intended to restrict" }, " the respective nature of these ", { b: "Optional Groups" }, ". Their purposes and guidelines are explained separately in the ", { b: "Community Notes section" }, " at the end of these guidelines."],
    ],
    items: [
      {
        icon: Store,
        label: "1 Business Promotions Group",
        text: [
          ["A dedicated space for members to promote their businesses, products, services, offers, launches, and other business-related promotional content ", { b: "shared with the intention of selling" }, "."],
          ["Members can also share their ", { b: "business-related social media content" }, ", including LinkedIn, Instagram, and other social media posts."],
          [{ b: "A maximum of one message a day" }, " is allowed in this group."],
        ],
      },
      {
        icon: Coffee,
        label: "1 Beyond Work Group",
        text: [["Casual conversations, friendships, shared interests, outings and ", { b: "member-initiated meetups" }, "."]],
      },
    ],
  },
];

const WELCOME = [
  {
    icon: Users,
    title: "Introduce Yourself",
    text: [
      [{ b: "Introduce yourself" }, " when joining a group. Share ", { b: "what you do" }, ", ", { b: "what you\u2019re looking for" }, ", and ", { b: "relevant profile links" }, " such as your website, LinkedIn, or Instagram."],
      [{ b: "Individual social media content links" }, " should ", { b: "not be shared in the main groups" }, ". You can ", { b: "reintroduce yourself" }, " from time to time as new members keep joining and the community grows."],
    ],
  },
  {
    icon: Handshake,
    title: "Ask & Share",
    text: [["Ask for ", { b: "referrals, recommendations, feedback, requirements, or opportunities" }, ". The ", { b: "clearer the requirement, the better the response" }, "."]],
  },
  {
    icon: Layers,
    title: "Use the Right Group",
    text: [
      ["Please refer to the ", { b: "HFA WhatsApp Community Structure" }, " above to understand which group is meant for what."],
      ["Use each group for its ", { b: "intended purpose" }, " and ", { b: "avoid posting the same or similar messages across multiple groups" }, "."],
    ],
  },
  {
    icon: Sparkles,
    title: "Queries, Feedback & Suggestions",
    text: [
      [{ b: "Constructive feedback" }, " is always welcome. Please share it ", { b: "directly with the concerned person" }, "."],
      [{ b: "Meetup-related" }, " queries, feedback & suggestions \u2192 ", { b: "City Captain" }],
      [{ b: "Other" }, " queries and suggestions \u2192 ", { b: "Admin Team" }],
      "We would love to keep improving the community with you.",
    ],
  },
  {
    icon: Coffee,
    title: "Meetups",
    text: [["Join ", { b: "online and offline meetups" }, " based on your availability."]],
  },
  {
    icon: MessageSquareText,
    title: "Message Before Calling",
    text: [["Message fellow members first and ", { b: "check their availability before calling" }, "."]],
  },
  {
    icon: Tag,
    title: "Add Your Member Tag",
    text: [
      ["Inside each WhatsApp group of the community, open the ", { b: "Members List" }, " and add a ", { b: "Member Tag representing your profession" }, " below your name, such as ", { i: "Software Developer, Chartered Accountant, Digital Marketer," }, " or ", { i: "Architect." }],
      ["Your Member Tag will be visible only within that particular HFA group, ", { b: "appearing below your name in the group chat" }, " and ", { b: "in the group\u2019s Members List" }, "."],
      ["This helps ", { b: "members quickly understand each other\u2019s professional background" }, " and connect more meaningfully."],
    ],
  },
  {
    icon: Heart,
    title: "Respectful Interactions",
    text: [
      ["Be ", { b: "kind, respectful, and considerate" }, " with everyone. ", { b: "Every member is equal" }, ", regardless of age, gender, profession, seniority, or position."],
      ["Please ", { b: "avoid arguments" }, " and be mindful of how you communicate. For any concerns or issues, please reach out to the admins directly."],
      [{ b: "Community decisions made by the City Captains or admins may reflect the preferences of the majority of members." }, " We appreciate everyone\u2019s understanding and cooperation."],
    ],
  },
];

const AVOID = [
  {
    icon: Megaphone,
    title: "Promotional Content",
    text: ["Avoid ", { b: "promotional broadcasts, advertisements, offers, or sales messages" }, " for products, services, properties, events, courses, or similar offerings in the ", { b: "Main Groups" }, ". ", { b: "Please use" }, " the dedicated ", { b: "Business Promotions group" }, " for the same, as described in the Community Notes below."],
  },
  {
    icon: GraduationCap,
    title: "Informational Content",
    text: ["Avoid ", { b: "generic educational, informational, awareness, or knowledge-sharing posts" }, " unless they are ", { b: "relevant to an ongoing discussion" }, " or ", { b: "specifically requested" }, " by members."],
  },
  {
    icon: Repeat2,
    title: "Forwarded Content",
    text: ["Avoid ", { b: "forwarded messages, chain messages, or unrelated content" }, "."],
  },
  {
    icon: PartyPopper,
    title: "Greetings, Wishes & Invitations",
    text: ["Avoid ", { b: "routine greetings-only messages, wishes, celebration messages, or invitations" }, " such as good morning, good night, birthdays, festivals, or similar occasions."],
  },
  {
    icon: Filter,
    title: "Repeated Posts",
    text: ["Avoid posting the ", { b: "same message across multiple groups" }, ". Share it ", { b: "only where it is most relevant" }, "."],
  },
  {
    icon: Share2,
    title: "External Group Links",
    text: ["Avoid sharing or promoting ", { b: "direct links or invitations" }, ", or discussing ", { b: "other WhatsApp groups, communities, professional networks, or online groups" }, "."],
  },
  {
    icon: BookOpenCheck,
    title: "Keep It Relevant",
    text: ["Keep conversations and content ", { b: "relevant to the purpose of the respective group" }, "."],
  },
  {
    icon: Moon,
    title: "Quiet Hours",
    text: ["Avoid messaging in the community groups or reaching out to members personally ", { b: "between 12:00 AM and 6:00 AM" }, ", ", { b: "unless urgent" }, "."],
  },
];

// Community Notes — supplementary information rendered as individual
// beige cards after the "If These Guidelines Aren't Followed" box.
// Each entry becomes its own card matching the "You're Welcome"
// section's layout (icon + title + body) exactly, so the visual
// weight, spacing and typography all belong to the same design
// language. To add another note, append `{ icon, title, text }` here.
const COMMUNITY_NOTES = [
  {
    icon: Info,
    title: "Official HFA WhatsApp Groups",
    text: [
      [{ b: "All official HFA WhatsApp groups" }, " of your City Chapter are organised ", { b: "under one WhatsApp Community" }, ", shared with you directly by the HFA team."],
      [{ b: "Other groups" }, " created by members or others are ", { b: "independent of HFA" }, " and are not managed or overseen by us. Please use your discretion before joining them."],
    ],
  },
  {
    icon: Award,
    title: "City Captain Structure",
    text: [
      ["Each City Chapter has ", { b: "City Captain(s)" }, " who support and represent the chapter and its members, including ", { b: "coordinating official meetups" }, " and being the point of contact for ", { b: "meetup-related queries" }, "."],
      ["To find them, go to the ", { b: "Members List" }, " of any group within your HFA Community and search for ", { b: "\u201CCaptain.\u201D" }, " You\u2019ll find their profiles with the ", { b: "City Captain" }, " Member Tag."],
      [{ b: "Members interested in becoming City Captains in the future can apply" }, " through the form link in the About section of every HFA WhatsApp group. The form includes details about its ", { b: "benefits and how Captains contribute" }, " to the community."],
    ],
  },
  {
    icon: Store,
    title: "Business Promotions Group",
    text: [
      [{ b: "Use this group" }, " to share business-related content with other HFA members, including:"],
      {
        bullets: [
          [{ b: "Promotions, products, services, offers, launches," }, " or other business-related content ", { b: "intended for selling." }],
          [{ b: "Business-related social media content" }, ", including LinkedIn, Instagram, YouTube videos, and other social media posts."],
        ],
      },
      [{ b: "Promotions or discussions" }, " related to ", { b: "other professional communities" }, ", networking communities, or similar membership-based communities are ", { b: "not permitted" }, " anywhere within the HFA WhatsApp Community."],
      ["Please ", { b: "avoid repeatedly posting" }, " the same or substantially similar promotions ", { b: "over consecutive days" }, "."],
      ["A ", { b: "maximum of one post per day" }, " is allowed in this group."],
    ],
  },
  {
    icon: Coffee,
    title: "Beyond Work Group",
    text: [
      ["A dedicated group within the HFA WhatsApp Community for members of your City Chapter to connect ", { b: "beyond professional discussions" }, ", expand your social circle, share interests, and ", { b: "plan casual meetups" }, "."],
      ["The ", { b: "Do\u2019s and Don\u2019ts above" }, " apply to the ", { b: "Main Groups" }, " and are ", { b: "not intended to restrict" }, " the casual nature of this group. Please keep the space ", { b: "kind, respectful" }, ", and ", { b: "free from spam, hateful content, and promotional broadcasting" }, "."],
      [{ b: "Please use this group" }, " for casual conversations, meetups, outings, and other activities ", { b: "instead of creating additional WhatsApp groups" }, " for individual plans, so there is no confusion among members due to multiple casual groups."],
    ],
  },
  {
    icon: ListChecks,
    title: "Which Groups to Join",
    text: [
      "Members are added to:",
      {
        bullets: [
          [{ b: "City Members Group" }],
          [{ b: "Professional Circle Group(s)" }, " relevant to their current work"],
        ],
      },
      "This keeps each Professional Circle focused while allowing members to connect across industries through the City Members Group.",
      [{ b: "If you want to join another Professional Circle" }, ", reach out to the admins and share which Circle(s) you\u2019d like to join and how your current work relates to them."],
      [{ b: "Additionally" }, ", you\u2019re welcome to join the ", { b: "Beyond Work" }, " and ", { b: "Business Promotions" }, " groups for their respective purposes."],
      [{ b: "All of these groups are available within your City Chapter\u2019s WhatsApp Community." }],
    ],
  },
  {
    icon: CalendarDays,
    title: "Meetups",
    text: [
      [{ b: "Official Cross-Industry Meetups:" }, " City Captains share details of official offline and online meetups in the Announcement Group and City Members Group."],
      [{ b: "Professional Circle Meetups:" }, " Members are encouraged to initiate online or offline meetups within their Professional Circle Groups."],
      [{ b: "Casual Meetups:" }, " Members are encouraged to initiate casual meetups, plans, outings, and activities within Beyond Work Group."],
    ],
  },
  {
    icon: ContactRound,
    title: "Ethical & Responsive Behaviour",
    text: [
      ["Members are expected to act responsibly, honour their commitments in a timely manner, and maintain ", { b: "honest and ethical conduct" }, " in the community."],
      [{ b: "Contact details of HFA members should not be used for unsolicited marketing" }, ", CRM databases, mass WhatsApp broadcasting, or promotional outreach on WhatsApp or any other platform."],
    ],
  },
  {
    icon: UserSearch,
    title: "Finding Members Based on Profession",
    text: [
      ["You can find members based on their profession using ", { b: "Member Tags" }, " in the ", { b: "Members List of the WhatsApp groups" }, " within your HFA Community."],
      ["In the ", { b: "City Members Group" }, " and other groups, most members have added their Member Tag, helping you understand their professional background. Looking for someone in a specific field? Search terms such as ", { b: "\u201CInterior,\u201D \u201CCA,\u201D \u201CMarketing,\u201D" }, " or ", { b: "\u201CSoftware\u201D" }, " in the ", { b: "Members List" }, " to find relevant members."],
      ["To avoid members getting ", { b: "unwanted calls, promotional messages, automated spam messages, and other forms of spam" }, ", we do not share separate member databases or spreadsheets containing personal contact details."],
      [{ b: "You can still discover and connect with relevant professionals directly through the community." }],
    ],
  },
];

const fade = (delay = 0) => ({
  initial: { opacity: 0, y: 14 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, amount: 0.15 },
  transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay },
});

export default function CommunityGuidelines() {
  const [searchParams] = useSearchParams();
  const cityParam = (searchParams.get("city") || "").trim().toLowerCase();
  const city = CITY_CHAPTERS.find((c) => c.toLowerCase() === cityParam) || "[City]";
  return (
    <div className="min-h-screen bg-white text-black font-sans">
      {/* Minimal header */}
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/85 border-b border-black/5">
        <div className="container-x flex items-center justify-between h-16 md:h-20">
          <Link
            to="/"
            data-testid="cg-back-home"
            className="inline-flex items-center gap-2 text-sm font-medium text-black/70 hover:text-black transition-colors"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="font-display font-bold text-base md:text-lg tracking-tight text-black">
              <BrandName />
            </span>
          </Link>
          <div className="text-xs uppercase tracking-[0.22em] text-black/45 font-semibold hidden sm:block">
            Community Guidelines
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="pt-12 pb-12 md:pt-16 md:pb-16 bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="cg-hero">
        <div
          aria-hidden="true"
          className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-orange-600/15 blur-3xl pointer-events-none"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-orange-600/10 blur-3xl pointer-events-none"
        />

        <div className="container-x relative">
          <motion.div {...fade(0)} className="max-w-3xl">
            <span className="inline-flex items-center gap-2.5 px-5 py-2 rounded-full font-display font-bold text-[13px] sm:text-sm md:text-[15px] uppercase tracking-[0.22em] bg-orange-600 text-white shadow-[0_10px_28px_-10px_rgba(234,88,12,0.7)]">
              <span className="w-1.5 h-1.5 rounded-full bg-white/95 animate-pulse-soft" />
              For Members
            </span>
            <h1
              data-testid="cg-title"
              className="font-display mt-6 text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-extrabold tracking-tightest leading-[1.02]"
            >
              Community Guidelines
            </h1>
            <p className="mt-5 text-[15px] md:text-base text-white/70 leading-relaxed max-w-4xl" data-testid="cg-hero-description">
              These guidelines are designed to keep HFA professional, relevant,
              welcoming, and valuable for everyone.
            </p>
          </motion.div>
        </div>
      </section>

      {/* How to Make the Most of HFA — editorial principle feature */}
      <section
        className="bg-white text-black pt-10 md:pt-14"
        data-testid="cg-most-section"
      >
        <div className="mx-auto w-full max-w-[1720px] px-6 md:px-8 lg:px-10 xl:px-12">
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            data-testid="cg-most-card"
            className="relative overflow-hidden rounded-3xl border border-orange-600/20 hfa-orange-surface text-black px-4 py-4 sm:px-6 sm:py-5 md:px-8 md:py-6"
          >
            <div className="relative">
              <div className="inline-flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-orange-600 text-white">
                  <Target className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2.25} />
                </span>
                <h2
                  data-testid="cg-most-heading"
                  className="font-display text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tightest leading-[1.05]"
                >
                  How to Make the Most of HFA
                </h2>
              </div>
              <div className="mt-4 space-y-4 md:space-y-5 text-[15px] md:text-base text-black/75 leading-relaxed pb-1 md:pb-2" data-testid="cg-most-body">
                <p data-testid="cg-most-core">
                  HFA becomes more valuable when you <strong className="font-semibold text-black">actively make yourself a part of the community</strong>.
                </p>
                <p>
                  <strong className="font-semibold text-black">Attend offline and online meetups</strong>, participate in or{" "}
                  <strong className="font-semibold text-black">initiate meaningful discussions</strong> in the WhatsApp community,
                  share your work and expertise, and take the time to connect with other members and{" "}
                  <strong className="font-semibold text-black">expand your social circle</strong>.
                </p>
                <p>
                  As people get to know you, <strong className="font-semibold text-black">familiarity, trust, and visibility</strong> build
                  naturally within the network. These relationships often lead to referrals, collaborations, hiring and work
                  opportunities, and other meaningful professional connections.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* HFA WhatsApp Community Structure — highlighted intro box + transition line */}
      <section
        className="bg-white text-black pt-10 md:pt-14 pb-10 md:pb-14"
        data-testid="cg-structure-section"
      >
        <div className="mx-auto w-full max-w-[1720px] px-6 md:px-8 lg:px-10 xl:px-12">
          <p
            data-testid="cg-hero-lead-in"
            className="mb-5 md:mb-6 text-[15px] md:text-base font-semibold text-black/90 leading-relaxed"
          >
            Before we start, here&rsquo;s how to use the HFA Community and its WhatsApp groups.
          </p>
          <motion.div
            initial={{ opacity: 0, y: 14 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, ease: [0.22, 1, 0.36, 1] }}
            data-testid="cg-structure-card"
            className="relative rounded-3xl border border-orange-600/25 hfa-orange-surface px-4 py-4 sm:px-6 sm:py-5 md:px-8 md:py-6 overflow-hidden shadow-[0_24px_60px_-40px_rgba(234,88,12,0.45)]"
          >
            <div
              aria-hidden="true"
              className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-orange-600/10 blur-3xl pointer-events-none"
            />
            <div className="relative">
              <div className="inline-flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-orange-600 text-white">
                  <Layers3 className="w-5 h-5 md:w-6 md:h-6" strokeWidth={2.25} />
                </span>
                <h2
                  data-testid="cg-structure-heading"
                  className="font-display text-2xl sm:text-3xl md:text-4xl font-extrabold tracking-tightest leading-[1.05]"
                >
                  HFA WhatsApp Community Structure
                </h2>
              </div>
              <p
                data-testid="cg-structure-subline"
                className="mt-4 text-[15px] md:text-base text-black/75 leading-relaxed"
              >
                <strong className="font-semibold text-black">Each city chapter</strong> has a{" "}
                <strong className="font-semibold text-black">separate WhatsApp Community</strong>, including the following groups.
                Here&rsquo;s a quick look at what each group is for.
              </p>

              <div className="mt-6 md:mt-8 grid grid-cols-1 md:grid-cols-[11fr_9fr] lg:grid-cols-[2fr_1fr] 2xl:grid-cols-[7fr_3fr] gap-4 md:gap-6 xl:gap-8">
                {STRUCTURE.map(({ heading, highlight, items, note, noteHeading }, gi) => (
                  <div
                    key={gi}
                    data-testid={`cg-structure-group-${gi}`}
                    className="bg-white border border-black/8 rounded-2xl px-4 py-4 sm:px-5 sm:py-4 md:px-6 md:py-5 xl:px-8 xl:py-6"
                  >
                    {highlight ? (
                      <div className="pb-3 border-b border-black/10">
                        <h3
                          data-testid={`cg-structure-group-${gi}-heading`}
                          className="inline-flex items-center rounded-xl bg-black text-white font-display font-extrabold text-xl md:text-2xl leading-none px-4 py-2.5 md:px-5 md:py-3 ring-1 ring-black/70 ring-offset-2 ring-offset-white"
                        >
                          {heading}
                        </h3>
                      </div>
                    ) : (
                      <h3 className="font-display font-extrabold text-xl md:text-2xl text-black leading-snug pb-3 border-b border-black/10">
                        {heading}
                      </h3>
                    )}
                    <ul className="mt-5 space-y-5">
                      {items.map(({ icon: Icon, label, groupName, text, pillsHeading, pills }, ii) => (
                        <li key={ii}>
                          <div className="flex">
                            <p
                              data-testid={`cg-structure-group-${gi}-item-${ii}-label`}
                              className="inline-flex items-center gap-2.5 rounded-lg bg-black text-white font-display text-base md:text-lg font-bold leading-none px-3.5 py-1.5 md:px-4 md:py-2"
                            >
                              <Icon className="w-5 h-5 shrink-0" strokeWidth={2.25} />
                              <span>{label}</span>
                            </p>
                          </div>
                          {groupName && (
                            <p
                              data-testid={`cg-structure-group-${gi}-item-${ii}-name`}
                              className="mt-2.5 text-[15px] md:text-base text-black/75 leading-relaxed"
                            >
                              Group name:{" "}
                              <span className="font-semibold text-black">{groupName(city)}</span>
                            </p>
                          )}
                          <div className={`${groupName ? "mt-1.5" : "mt-2.5"} space-y-3 text-[15px] md:text-base text-black/75 leading-relaxed`}>
                            {(Array.isArray(text) ? text : [text]).map((para, pi) => (
                              <p key={pi}>{renderRich(para)}</p>
                            ))}
                          </div>
                          {pills && (
                            <div className="mt-4" data-testid={`cg-structure-group-${gi}-item-${ii}-pills`}>
                              <p className="text-[15px] md:text-base font-semibold text-black leading-snug">
                                {pillsHeading}
                              </p>
                              <ul className="mt-2.5 flex flex-wrap gap-2">
                                {pills.map((name) => (
                                  <li
                                    key={name}
                                    className="inline-flex items-center rounded-full hfa-orange-surface border border-orange-600/25 px-3 py-1.5 text-[13px] md:text-sm font-medium text-black leading-none"
                                  >
                                    {name}
                                  </li>
                                ))}
                              </ul>
                            </div>
                          )}
                        </li>
                      ))}
                    </ul>
                    {note && (
                      <div
                        data-testid={`cg-structure-group-${gi}-note`}
                        className="mt-5 pt-4 border-t border-black/8 space-y-3 text-[15px] md:text-base text-black/60 leading-relaxed"
                      >
                        {noteHeading && (
                          <p
                            data-testid={`cg-structure-group-${gi}-note-heading`}
                            className="inline-block rounded-lg bg-black text-white font-semibold text-[15px] md:text-base leading-snug px-3.5 py-2"
                          >
                            {noteHeading}
                          </p>
                        )}
                        {(Array.isArray(note) ? note : [note]).map((n, ni) => (
                          <p key={ni}>{renderRich(n)}</p>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </motion.div>

        </div>
      </section>

      {/* Full-width transition ribbon (same dark/orange treatment as the hero) */}
      <section
        data-testid="cg-structure-transition"
        className="relative overflow-hidden bg-[#0A0A0A] text-white py-7 md:py-9"
      >
        <div
          aria-hidden="true"
          className="absolute -top-24 -right-24 w-96 h-96 rounded-full bg-orange-600/15 blur-3xl pointer-events-none"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-24 -left-24 w-96 h-96 rounded-full bg-orange-600/10 blur-3xl pointer-events-none"
        />
        <div className="container-x relative">
          <motion.p
            {...fade(0.08)}
            className="max-w-[1000px] mx-auto text-center text-base md:text-lg text-white/75 leading-relaxed [&_strong]:text-white"
          >
            {renderRich([
              "The following ",
              { b: "guidelines (Do\u2019s and Don\u2019ts) apply to the Main Groups" },
              " as mentioned above. The ",
              { b: "two Optional Groups" },
              " have their own ",
              { b: "distinct purposes" },
              " and notes, outlined ",
              { b: "separately" },
              " in the ",
              { b: "Community Notes section" },
              " below:",
            ])}
          </motion.p>
        </div>
      </section>

      {/* Welcome to */}
      <section className="pt-10 pb-12 md:pt-14 md:pb-16 bg-white text-black" data-testid="cg-welcome-section">
        <div className="container-x">
          <motion.div {...fade(0)} className="max-w-3xl">
            <div className="inline-flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-green-100 text-green-700">
                <Check className="w-5 h-5 md:w-6 md:h-6" strokeWidth={3} />
              </span>
              <h2
                data-testid="cg-welcome-heading"
                className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tightest leading-[1.02]"
              >
                Do&rsquo;s
              </h2>
            </div>
            <p className="mt-4 text-[15px] md:text-base text-black/60 leading-relaxed max-w-xl">
              What makes this community thrive.
            </p>
          </motion.div>

          <div className="mt-8 md:mt-10 flex flex-col gap-4 md:block md:columns-2 md:gap-5 md:-mb-5">
            {WELCOME.map(({ icon: Icon, title, text }, i) => (
              <motion.div
                key={i}
                {...fade(0.03 * i)}
                data-testid={`cg-welcome-${i}`}
                className="group md:break-inside-avoid md:mb-5 hfa-orange-surface border border-orange-600/20 rounded-2xl px-6 py-5 md:px-7 md:py-5 hover:border-orange-600/45 hover:-translate-y-0.5 transition-all duration-300"
              >
                <div>
                  <h3
                    data-testid={`cg-welcome-${i}-title`}
                    className="inline-flex items-center gap-2 rounded-lg bg-black text-white font-display font-bold text-[15px] md:text-base leading-none px-3 py-1.5 group-hover:bg-orange-600 transition-colors"
                  >
                    <Icon className="shrink-0 w-4 h-4" aria-hidden="true" />
                    <span>{title}</span>
                  </h3>
                </div>
                {Array.isArray(text) ? (
                  <div className="mt-4 space-y-5 text-[15px] md:text-base text-black/75 leading-relaxed">
                    {text.map((para, pi) => (
                      <p key={pi}>{renderRich(para)}</p>
                    ))}
                  </div>
                ) : (
                  <p className="mt-4 text-[15px] md:text-base text-black/75 leading-relaxed">
                    {text}
                  </p>
                )}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Please avoid — full black contrast */}
      <section className="py-12 md:py-16 bg-[#0A0A0A] text-white relative overflow-hidden" data-testid="cg-avoid-section">
        <div
          aria-hidden="true"
          className="absolute -top-24 -left-24 w-96 h-96 rounded-full bg-orange-600/10 blur-3xl pointer-events-none"
        />
        <div className="container-x relative">
          <motion.div {...fade(0)} className="max-w-3xl">
            <div className="inline-flex items-center gap-3">
              <span className="inline-flex items-center justify-center w-10 h-10 md:w-12 md:h-12 rounded-full bg-orange-600 text-white">
                <X className="w-5 h-5 md:w-6 md:h-6" strokeWidth={3} />
              </span>
              <h2
                data-testid="cg-avoid-heading"
                className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tightest leading-[1.02] text-white"
              >
                Don&rsquo;ts
              </h2>
            </div>
            <p className="mt-4 text-[15px] md:text-base text-white/55 leading-relaxed max-w-xl">
              A few things that dilute the experience.
            </p>
          </motion.div>

          <div className="mt-8 md:mt-10 flex flex-col gap-4 md:block md:columns-2 md:gap-5 md:-mb-5">
            {AVOID.map(({ icon: Icon, title, text }, i) => (
              <motion.div
                key={i}
                {...fade(0.03 * i)}
                data-testid={`cg-avoid-${i}`}
                className="group md:break-inside-avoid md:mb-5 bg-white/[0.04] border border-white/10 rounded-2xl px-6 py-5 md:px-7 md:py-5 hover:border-orange-600/40 hover:bg-white/[0.06] hover:-translate-y-0.5 transition-all duration-300"
              >
                <div>
                  <h3
                    data-testid={`cg-avoid-${i}-title`}
                    className="inline-flex items-center gap-2 rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 text-white font-display font-bold text-[15px] md:text-base leading-none px-3 py-1.5 shadow-[0_2px_10px_rgba(234,88,12,0.25)]"
                  >
                    <Icon className="shrink-0 w-4 h-4" aria-hidden="true" />
                    <span>{title}</span>
                  </h3>
                </div>
                <p className="mt-4 text-[15px] md:text-base text-white/75 leading-relaxed">
                  {renderRich(text, true)}
                </p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Closing note */}
      <section className="py-12 md:py-16 bg-white text-black" data-testid="cg-closing-section">
        <div className="container-x">
          <motion.section
            {...fade(0)}
            data-testid="cg-community-notes-section"
            aria-labelledby="cg-community-notes-heading"
          >
            <div className="flex flex-col items-center text-center mb-6 md:mb-8">
              <h2
                data-testid="cg-community-notes-heading"
                id="cg-community-notes-heading"
                className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tightest leading-[1.02] text-black"
              >
                Community Notes
              </h2>
            </div>
            <div className="flex flex-col gap-4 md:block md:columns-2 md:gap-5 md:-mb-5">
              {COMMUNITY_NOTES.map(({ icon: Icon, title, text }, i) => (
                <motion.div
                  key={i}
                  {...fade(0.03 * i)}
                  data-testid={`cg-community-note-${i}`}
                  className="group md:break-inside-avoid md:mb-5 hfa-orange-surface border border-orange-600/20 rounded-2xl px-6 py-5 md:px-7 md:py-5 hover:border-orange-600/45 hover:-translate-y-0.5 transition-all duration-300"
                >
                  <div>
                    <h3
                      data-testid={`cg-community-note-${i}-title`}
                      className="inline-flex items-center gap-2.5 rounded-lg bg-black text-white font-display text-base md:text-lg font-bold leading-none px-3.5 py-1.5 md:px-4 md:py-2 group-hover:bg-orange-600 transition-colors"
                    >
                      <Icon className="shrink-0 w-4 h-4 md:w-[18px] md:h-[18px]" aria-hidden="true" />
                      <span>{title}</span>
                    </h3>
                  </div>
                  {Array.isArray(text) ? (
                    <div className="mt-4 space-y-5 text-[15px] md:text-base text-black/75 leading-relaxed">
                      {text.map((para, pi) =>
                        typeof para === "string" || Array.isArray(para) ? (
                          <p key={pi}>{renderRich(para)}</p>
                        ) : (
                          <ul key={pi} className="list-disc pl-5 space-y-1.5 !mt-3">
                            {para.bullets.map((b, bi) => (
                              <li key={bi}>{renderRich(b)}</li>
                            ))}
                          </ul>
                        )
                      )}
                    </div>
                  ) : (
                    <p className="mt-4 text-[15px] md:text-base text-black/75 leading-relaxed">
                      {text}
                    </p>
                  )}
                </motion.div>
              ))}
            </div>
          </motion.section>

          <motion.div
            {...fade(0)}
            data-testid="cg-philosophy-card"
            className="mt-10 md:mt-14 relative max-w-4xl mx-auto rounded-3xl border border-black/8 bg-[#FAF7F2] px-6 py-5 md:px-10 md:py-7 overflow-hidden"
          >
            <div
              aria-hidden="true"
              className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-orange-600/8 blur-3xl pointer-events-none"
            />
            <div className="relative space-y-5 text-[15px] md:text-base text-black/75 leading-relaxed max-w-3xl">
              <p>
                If you&rsquo;re ever unsure whether a post is appropriate for
                the community, feel free to DM the admins of that group.
                We&rsquo;d be happy to guide you.
              </p>
            </div>
          </motion.div>

          {/* Orange pill transition heading + subtle downward indicator */}
          <motion.div
            {...fade(0.08)}
            data-testid="cg-transition"
            className="mt-10 md:mt-12 flex flex-col items-center text-center"
          >
            <span
              data-testid="cg-transition-heading"
              className="inline-flex items-center gap-2 rounded-full bg-orange-600 text-white text-[11px] md:text-xs font-semibold uppercase tracking-[0.18em] px-4 py-2 shadow-[0_10px_26px_-14px_rgba(234,88,12,0.55)]"
            >
              If These Guidelines Aren't Followed
            </span>
            <motion.div
              aria-hidden="true"
              initial={{ y: 0, opacity: 0.55 }}
              animate={{ y: [0, 6, 0], opacity: [0.55, 0.95, 0.55] }}
              transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
              className="mt-4 text-orange-600/80"
              data-testid="cg-transition-indicator"
            >
              <ChevronDown className="w-5 h-5" strokeWidth={1.75} />
            </motion.div>
          </motion.div>

          <motion.div
            {...fade(0)}
            className="mt-6 md:mt-8 relative max-w-4xl mx-auto rounded-3xl border border-black/10 bg-[#0A0A0A] text-white px-6 py-5 md:px-10 md:py-7 overflow-hidden"
          >
            <div
              aria-hidden="true"
              className="absolute -top-16 -right-12 w-72 h-72 rounded-full bg-orange-600/25 blur-3xl pointer-events-none"
            />
            <div
              data-testid="cg-closing-text"
              className="relative text-[15px] md:text-base text-white/85 leading-relaxed max-w-3xl space-y-4"
            >
              <p>
                <strong className="font-semibold text-white">Posts</strong> that{" "}
                <strong className="font-semibold text-white">don&rsquo;t align</strong> with these guidelines may be{" "}
                <strong className="font-semibold text-white">removed</strong>.{" "}
                <strong className="font-semibold text-white">Members who repeatedly don&rsquo;t follow these guidelines will be removed from the community without warning</strong>, with{" "}
                <strong className="font-semibold text-white">no refund</strong>.
              </p>
              <p>
                We appreciate your support in helping us maintain a thoughtful and
                valuable community for everyone.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Instagram profile — same shared card as the /apply success screen */}
      <section className="bg-white text-black pb-12 md:pb-16" data-testid="cg-instagram-section">
        <div className="container-x flex justify-center">
          <InstagramProfileCard testid="cg-instagram" className="flex flex-col items-center text-center" />
        </div>
        <div className="container-x">
          <motion.div
            {...fade(0.08)}
            data-testid="cg-back-cta-wrapper"
            className="mt-10 md:mt-12 flex justify-center"
          >
            <Link
              to="/"
              data-testid="cg-back-cta"
              className="inline-flex items-center gap-2 text-orange-600 hover:text-orange-500 text-sm font-semibold transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              <span>Back to <BrandName /></span>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="border-t border-black/5 bg-white py-8">
        <div className="container-x text-center sm:text-left text-xs text-black/45">
          © {new Date().getFullYear()} <BrandName />. All rights reserved.
        </div>
      </footer>
    </div>
  );
}
